import trackingService from "app/pages/sales-intelligence/common-components/feature-upgrade/services/tracking/trackingService";
import { APPS_SDKS_HOOK_ID, SI_INTENT_HOOK_ID } from "app/pages/sales-intelligence/constants/common";
import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import intentTrackingService from "app/pages/sales-intelligence/services/tracking/intent/trackingService";
import { showIntentUpsellTopicHook } from "app/pages/sales-intelligence/sub-modules/intent/internalHooks/hooks";
import type { CustomAttributes } from "app/services/track/track";
import { requestFeature } from "pro-features/components/Modals/src/HookModal/services/requestFeature";

/**
 * Apps signals are gated on `isAppsSdksAvailable`, so the upsell sells that same claim. The lead-generator
 * hook this used to open sells a different product, gated on `isConsolidatedAppsEnabled`.
 */
export const requestAppsSignalsAccess = (): void => {
    trackingService.trackFeatureAccessRequested(APPS_SDKS_HOOK_ID);
    void requestFeature(APPS_SDKS_HOOK_ID);
};

/** Opens the hook modal, as intent's company-analysis and manage-signals surfaces do. */
export const requestIntentSignalsAccess = (): void => {
    void intentTrackingService.trackIntentHook("intent hook", "open popup");
    showIntentUpsellTopicHook();
};

/**
 * The management modal's intent banner submits a sales request rather than opening the hook modal, so it
 * tracks as an upgrade banner like every other one -- keeping intent and apps upsells comparable.
 */
export const requestIntentDataAccess = (customAttributes?: CustomAttributes): void => {
    trackingService.trackFeatureAccessRequested(SI_INTENT_HOOK_ID, customAttributes);
    void requestFeature(SI_INTENT_HOOK_ID);
};

/**
 * Which sales request a locked type submits from the management modal. Partial by design: only a type
 * the service can actually lock needs an entry. A missing entry means the banner's CTA does nothing at
 * all, so `requestSignalTypeAccess.spec` pins the two lockable types explicitly.
 */
const requestAccessBySignalType: Partial<Record<SignalType, VoidFunction>> = {
    [signalTypeMap.apps]: requestAppsSignalsAccess,
    [signalTypeMap.intent]: requestIntentDataAccess,
};

/** Exported for the guard spec so it need not reach into the map. */
export const requestableSignalTypes = Object.keys(requestAccessBySignalType) as readonly SignalType[];

export const requestSignalTypeAccessBySignalType = (signalType: SignalType): void => {
    requestAccessBySignalType[signalType]?.();
};
